import { motion } from "framer-motion";
import { forwardRef, ReactNode } from "react";

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

export const FadeUp = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
FadeUp.displayName = "FadeUp";

export const FadeIn = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
FadeIn.displayName = "FadeIn";

export const ScaleIn = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
ScaleIn.displayName = "ScaleIn";

export const SlideInLeft = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
SlideInLeft.displayName = "SlideInLeft";

export const SlideInRight = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
SlideInRight.displayName = "SlideInRight";

export const BlurIn = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
BlurIn.displayName = "BlurIn";

export const RotateIn = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, rotate: -5, scale: 0.95 }}
      whileInView={{ opacity: 1, rotate: 0, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
RotateIn.displayName = "RotateIn";

export const FlipUp = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, rotateX: 20, y: 30 }}
      whileInView={{ opacity: 1, rotateX: 0, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
      style={{ perspective: 1000 }}
    >
      {children}
    </motion.div>
  )
);
FlipUp.displayName = "FlipUp";

export const StaggerContainer = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      transition={{ staggerChildren: 0.12, delayChildren: delay }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
StaggerContainer.displayName = "StaggerContainer";

export const StaggerItem = forwardRef<HTMLDivElement, { children: ReactNode; className?: string }>(
  ({ children, className }, ref) => (
    <motion.div
      ref={ref}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.4, 0.25, 1] } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
StaggerItem.displayName = "StaggerItem";

export const ScaleStaggerItem = forwardRef<HTMLDivElement, { children: ReactNode; className?: string }>(
  ({ children, className }, ref) => (
    <motion.div
      ref={ref}
      variants={{
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: [0.25, 0.4, 0.25, 1] } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
ScaleStaggerItem.displayName = "ScaleStaggerItem";

export const TextReveal = forwardRef<HTMLDivElement, AnimatedSectionProps>(
  ({ children, className, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20, clipPath: "inset(100% 0 0 0)" }}
      whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0 0)" }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
);
TextReveal.displayName = "TextReveal";
